import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import { setWindowLocation } from '@eb/http';
import { translationPropType } from '@eb/i18n';

import { BucketHeader } from '../BucketHeader';

const ArticleCardGroup = loadable(() => import('./ArticleCardGroup'), {
    resolveComponent: (components) => components.ArticleCardGroup,
});
const FeaturedArticleCardGroup = loadable(() => import('./ArticleCardGroup'), {
    resolveComponent: (components) => components.FeaturedArticleCardGroup,
});
const BigEventCardGroup = loadable(() => import('./BigEventCardGroup'), {
    resolveComponent: (components) => components.BigEventCardGroup,
});
const SmallEventCardGroup = loadable(() => import('./SmallEventCardGroup'), {
    resolveComponent: (components) => components.SmallEventCardGroup,
});
const ListEventCardGroup = loadable(() => import('./ListEventCardGroup'), {
    resolveComponent: (components) => components.ListEventCardGroup,
});
const LoadingCards = loadable(() => import('../LoadingCards'), {
    resolveComponent: (components) => components.LoadingCards,
});

import {
    FEATURED_TEMPLATE,
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    LIST_CARD_TEMPLATE,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    CARD_BIG,
    ARTICLE_TEMPLATE,
} from '../../constants';
import { GA_CITY_BROWSE_CATEGORY } from '../../constants/analytics';

const CARD_GROUP_MAP = {
    [ARTICLE_TEMPLATE]: ArticleCardGroup,
    [FEATURED_TEMPLATE]: FeaturedArticleCardGroup,
    [LARGE_CARD_TEMPLATE_3]: BigEventCardGroup,
    [LARGE_CARD_TEMPLATE_6]: BigEventCardGroup,
    [LIST_CARD_TEMPLATE]: ListEventCardGroup,
    [CARD_TEMPLATE_4]: SmallEventCardGroup,
    [CARD_TEMPLATE_8]: SmallEventCardGroup,
    [CARD_BIG]: BigEventCardGroup,
};

export const CardBucket = (props) => {
    const {
        content,
        name,
        template,
        linkUrl,
        isAuthenticated,
        experiments,
        featureFlags,
        trackGAEvent,
        trackGAEventOnPageUnload,
        bucketKey,
        isLoading,
        trackSeeMore,
        onTrackEventClickInternal,
    } = props;

    const DynamicCardGroup = CARD_GROUP_MAP[template];

    const isSmallCity = template === CARD_BIG;

    const handleTrackClick = React.useCallback(
        ({ url, id }, action, organizerFollowerCount) => {
            if (bucketKey) {
                trackGAEvent({
                    category: GA_CITY_BROWSE_CATEGORY,
                    action,
                    label: bucketKey,
                    dimensions: {
                        eventId: id,
                    },
                    metrics: {
                        organizerFollowerCount: organizerFollowerCount || 0,
                    },
                });
            }

            /* Need to fire tracking action on the seemore, so necessary to
            manually set windowlocation rather that relying on the link. */
            if (url) {
                setWindowLocation(url);
            }
        },
        [bucketKey, trackGAEvent],
    );

    const handleTrackClickInternal = React.useCallback(
        (index, id, ticketsBy) => {
            const trackingData = {
                clickRank: index,
                navigation: `city_browse.${bucketKey}`,
                itemId: id,
            };

            onTrackEventClickInternal &&
                onTrackEventClickInternal(trackingData, ticketsBy);
        },
        [bucketKey, onTrackEventClickInternal],
    );

    return (
        <div>
            <BucketHeader
                title={name}
                trackSeeMore={trackSeeMore}
                linkUrl={linkUrl}
                template={template}
                trackGAEvent={trackGAEvent}
                bucketKey={bucketKey}
            />
            {isLoading ? (
                <section className="eds-l-pad-left-6">
                    <LoadingCards cardCount={3} />
                </section>
            ) : (
                <DynamicCardGroup
                    template={template}
                    cards={content}
                    linkUrl={linkUrl}
                    isAuthenticated={isAuthenticated}
                    onTrackClick={handleTrackClick}
                    onTrackClickInternal={handleTrackClickInternal}
                    trackGAEvent={trackGAEvent}
                    trackGAEventOnPageUnload={trackGAEventOnPageUnload}
                    isSmallCity={isSmallCity}
                    title={name}
                    experiments={experiments}
                    featureFlags={featureFlags}
                />
            )}
        </div>
    );
};

CardBucket.propTypes = {
    /*
     * Array of preformatted objects that will represent that cards within the bucket
     */
    content: PropTypes.arrayOf(PropTypes.object).isRequired,
    /*
     * The manner in which this CardBucket should display its content
     */
    template: PropTypes.string.isRequired,
    /*
     * The name of the given bucket ie. "Upcoming Music Events"
     */
    name: translationPropType,
    /*
     * The url clicking "See More" should connect the user to
     */
    linkUrl: PropTypes.string,
    /*
     * Is user authenticated?
     */
    isAuthenticated: PropTypes.bool,
    /*
     * The key of the associated bucket
     */
    bucketKey: PropTypes.string,
    /*
     * Tracking callback for event clicks
     */
    onTrackEventClickInternal: PropTypes.func,
    /*
     * Track GA Events from state
     */
    trackGAEvent: PropTypes.func,
    /*
     * Track GA Events from state
     */
    trackGAEventOnPageUnload: PropTypes.func,
    isLoading: PropTypes.bool,
};

export default CardBucket;
